import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  AccountUsageDashboardDto,
  AccountUsageLimitDto,
  DataQualityFlag
} from '../models/account-usage.model';
import { PlanUsageApiService } from './plan-usage-api.service';

const STALE_FLAGS: DataQualityFlag[] = [
  'source_unavailable',
  'usage_not_evaluated',
  'snapshot_stale',
  'source_error'
];

@Injectable({ providedIn: 'root' })
export class PlanLimitStateService {
  private readonly usageSubject = new BehaviorSubject<AccountUsageDashboardDto | null>(null);
  readonly usage$: Observable<AccountUsageDashboardDto | null> = this.usageSubject.asObservable();

  constructor(private readonly api: PlanUsageApiService) {}

  refresh(accountId?: number): Observable<AccountUsageDashboardDto | null> {
    return this.api.getAccountUsage({ accountId }).pipe(
      tap((dto) => this.usageSubject.next(dto)),
      catchError(() => of(null))
    );
  }

  setSnapshot(snapshot: AccountUsageDashboardDto | null): void {
    this.usageSubject.next(snapshot);
  }

  getSnapshot(): AccountUsageDashboardDto | null {
    return this.usageSubject.value;
  }

  getLimit(limitCode: string): AccountUsageLimitDto | null {
    const snapshot = this.usageSubject.value;
    if (!snapshot) {
      return null;
    }
    return snapshot.limits.find((limit) => limit.limitCode === limitCode) ?? null;
  }

  /**
   * Advisory check. Returns true only when ALL of the following hold:
   *   - enforcementMode === 'hard'
   *   - overLimit === true
   *   - overageAllowed === false
   *   - dataQuality contains none of: source_unavailable, usage_not_evaluated,
   *     snapshot_stale, source_error
   *
   * The backend remains the authority when the action is actually executed;
   * use this only to preventively disable UI controls.
   */
  isBlocking(limitCode: string): boolean {
    const limit = this.getLimit(limitCode);
    if (!limit) {
      return false;
    }
    if (limit.enforcementMode !== 'hard') {
      return false;
    }
    if (!limit.overLimit) {
      return false;
    }
    if (limit.overageAllowed) {
      return false;
    }
    const flags = Array.isArray(limit.dataQuality) ? limit.dataQuality : [];
    return !STALE_FLAGS.some((flag) => flags.includes(flag));
  }
}
